import React, { createContext, useContext, useState, useEffect } from 'react';
import { readUrlParam } from '../lib/urlParams';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextValue {
    mode: ThemeMode;
    resolvedMode: 'light' | 'dark';
    setMode: (m: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
    mode: 'system',
    resolvedMode: 'light',
    setMode: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mode, setModeState] = useState<ThemeMode>(() => {
        // `?forceTheme=dark|light` pins the theme for one visit. It exists so a
        // screenshot run, a shared link or a bug report can name the theme it
        // means, instead of inheriting whatever the device happens to prefer.
        const forced = readUrlParam('forceTheme');
        if (forced === 'dark' || forced === 'light') return forced;
        try {
            const saved = localStorage.getItem('theme');
            if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
        } catch { /* Theme switching still works when browser storage is blocked. */ }
        return 'dark';
    });

    const [systemDark, setSystemDark] = useState(
        window.matchMedia('(prefers-color-scheme: dark)').matches
    );

    // Listen for system preference changes
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const resolvedMode: 'light' | 'dark' = mode === 'system'
        ? (systemDark ? 'dark' : 'light')
        : mode;

    /**
     * Switch the theme. Nothing else.
     *
     * Colour used to be defined twice — once here as inline custom properties on
     * <html>, and once in design/broadsheet.css. Inline styles win over a
     * stylesheet, so the CSS palette could never take effect and the two
     * definitions drifted apart; that is why surfaces looked identical in both
     * themes. broadsheet.css is now the single source of truth, and this effect
     * only tells it which palette to use.
     */
    useEffect(() => {
        const root = document.documentElement;

        // Tailwind is configured `darkMode: 'class'`, so every `dark:` utility in
        // the app keys off this class — not off data-theme. Without it the
        // resume's 60+ dark variants could never fire and /about was stuck
        // light while the rest of the site is dark.
        root.classList.toggle('dark', resolvedMode === 'dark');
        root.setAttribute('data-theme', resolvedMode);
        root.style.colorScheme = resolvedMode;
        const ground = getComputedStyle(root).getPropertyValue('--bs-ground').trim();
        if (ground) document.querySelector('meta[name="theme-color"]')?.setAttribute('content', ground);
    }, [resolvedMode]);

    const setMode = (m: ThemeMode) => {
        setModeState(m);
        try { localStorage.setItem('theme', m); } catch { /* Keep this visit's preference in memory. */ }
    };

    return (
        <ThemeContext.Provider value={{ mode, resolvedMode, setMode }}>
            {children}
        </ThemeContext.Provider>
    );
};
